import type {
  CellValue,
  ReportSheetConfig,
} from './report-config.types';
import type { CellStyle } from './report-style.types';

/**
 * Validación de datos de una celda. Por ahora solo lista desplegable
 * inline (las opciones viajan embebidas, sin hoja auxiliar).
 */
export interface CellDataValidation {
  readonly type: 'list';
  /** Opciones del desplegable. */
  readonly values: ReadonlyArray<string>;
  /** Permite dejar la celda vacía. Default true. */
  readonly allowBlank?: boolean;
}

/**
 * Celda posicionada de una hoja-formulario. A diferencia del modelo
 * tabular (columns + rows), aquí cada celda declara su referencia
 * absoluta ('B9') y, opcionalmente, el rango que mergea.
 */
export interface FormCell {
  /** Referencia ancla, ej. 'B9'. */
  readonly ref: string;
  readonly value?: CellValue;
  /** Desplegable de validación (ej. Tipo de Operación). */
  readonly dataValidation?: CellDataValidation;
  /**
   * Fórmula Excel SIN el `=` inicial (ej. 'TODAY()'). Si está
   * presente, tiene prioridad sobre `value`: la celda guarda la
   * fórmula viva y Excel la recalcula al abrir el archivo.
   */
  readonly formula?: string;
  readonly numFmt?: string;
  /**
   * El estilo se aplica a TODO el rango mergeado, no solo al ancla:
   * en un merge, Excel dibuja los bordes de las celdas subyacentes.
   */
  readonly style?: Partial<CellStyle>;
  /** Rango a mergear con `ref` como ancla, ej. 'B9:D9'. */
  readonly merge?: string;
}

/**
 * Imagen embebida en una hoja-formulario (ej. un logo). Los bytes
 * viajan SIEMPRE como base64: exceljs en browser no referencia paths
 * de archivo, necesita el contenido. El anclaje es por celda
 * top-left + extensión en píxeles — esto PRESERVA la proporción
 * nativa del archivo; anclar a un rango de celdas (stretch) la
 * deformaría.
 */
export interface FormImage {
  /** Contenido del archivo en base64 (sin el prefijo `data:`). */
  readonly base64: string;
  readonly extension: 'png' | 'jpeg' | 'gif';
  /**
   * Esquina superior-izquierda en coordenadas de celda 0-based
   * (col 0 = A, row 0 = fila 1). Admite fracciones para correr la
   * imagen dentro de una celda (ej. col 4.4 = 40% dentro de E).
   */
  readonly tl: { readonly col: number; readonly row: number };
  /** Tamaño en píxeles. Respetá la proporción nativa del archivo. */
  readonly ext: { readonly width: number; readonly height: number };
}

/** Márgenes de impresión en pulgadas. */
export interface FormPageMargins {
  readonly left?: number;
  readonly right?: number;
  readonly top?: number;
  readonly bottom?: number;
  readonly header?: number;
  readonly footer?: number;
}

/**
 * Configuración de impresión de una hoja-formulario. Sin esto Excel
 * elige los saltos de página por su cuenta y al exportar a PDF un
 * formato ancho se parte en varias carillas. Replica el `pageSetup`
 * del .xlsx fuente (orientación + márgenes + área de impresión).
 */
export interface FormPageSetup {
  readonly orientation?: 'portrait' | 'landscape';
  /**
   * Escala manual de impresión (%). MUTUAMENTE EXCLUYENTE con
   * `fitToWidth`/`fitToHeight`: si se define alguno de esos dos, Excel
   * ignora `scale` y manda el ajuste a página.
   */
  readonly scale?: number;
  /**
   * Páginas de ANCHO a las que ajustar el contenido (fit-to-page).
   * `1` = todo el ancho en una sola carilla.
   */
  readonly fitToWidth?: number;
  /** Igual que `fitToWidth` para el ALTO. */
  readonly fitToHeight?: number;
  readonly margins?: FormPageMargins;
  /** Área de impresión, ej. 'A1:P50'. Acota lo que entra al PDF. */
  readonly printArea?: string;
}

/**
 * Hoja de formato libre (formularios imprimibles): celdas sueltas,
 * merges, anchos de columna y altos de fila explícitos.
 */
export interface FormSheetConfig {
  readonly kind: 'form';
  readonly name: string;
  /** Anchos por columna empezando en A. */
  readonly columnWidths?: ReadonlyArray<number>;
  /** Altos por número de fila (1-based). */
  readonly rowHeights?: Readonly<Record<number, number>>;
  readonly cells: ReadonlyArray<FormCell>;
  /** Imágenes embebidas (logos), ancladas por píxeles. */
  readonly images?: ReadonlyArray<FormImage>;
  /** Default false: los formularios ocultan la grilla. */
  readonly showGridLines?: boolean;
  /** Zoom inicial de la hoja (%). Si se omite, Excel usa 100. */
  readonly zoomScale?: number;
  /**
   * Configuración de impresión (orientación, escala/fit-to-page,
   * márgenes, área). Sin esto el formato se parte en varias páginas
   * al exportar a PDF.
   */
  readonly pageSetup?: FormPageSetup;
}

export function isFormSheet(
  sheet: ReportSheetConfig,
): sheet is FormSheetConfig {
  return 'kind' in sheet && sheet.kind === 'form';
}
